import { SyntheticEvent, useEffect, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useParams } from "react-router";
import { useAppSelector } from "../../app/hooks";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import {
	TutorialCategoryViewModel,
	TutorialViewModel,
	useGetCategoriesQuery,
	useGetTutorialsQuery,
	usePlayTutorialQuery,
	useUpdateSessionTimeMutation,
} from "./lessonsApiSlice";

export default function Lesson() {
	const { codeName } = useParams();

	const [view, setView] = useState<string>("lesson");
	const [lesson, setLesson] = useState<TutorialViewModel | undefined>(undefined);
	const [lessonUrl, setLessonUrl] = useState<string | undefined>(undefined);
	const [category, setCategory] = useState<TutorialCategoryViewModel | undefined>(undefined);
	const [lessonFrameLoaded, setLessonFrameLoaded] = useState(false);

	const { aspUrl } = useAppSelector((state) => state.site);

	// Tutorials and categories should be cached
	const { data: tutorials } = useGetTutorialsQuery();
	const { data: categories } = useGetCategoriesQuery();

	const [updateSessionTime, updateSessionTimeResult] = useUpdateSessionTimeMutation();

	const result = usePlayTutorialQuery(codeName ?? skipToken);
	const play = result.data;

	function handleFrameLoad(e: SyntheticEvent<HTMLIFrameElement>) {
		var lessonDocument = e.currentTarget.contentDocument?.documentElement;
		setLessonFrameLoaded(true);
	}

	useEffect(() => {
		if (!tutorials) return setLesson(undefined);
		const _lesson = tutorials.find((t) => t.codeName == codeName);
		if (!_lesson) return setLesson(undefined);
		setLesson(_lesson);
	}, [codeName, tutorials]);

	useEffect(() => {
		if (!lesson) return setLessonUrl(undefined);
		setLessonUrl(`${aspUrl}/lessons/html/${lesson.location}`);
	}, [lesson]);

	useEffect(() => {
		if (!lesson || !categories) return setCategory(undefined);
		setCategory(categories.find((c) => c.categoryId == lesson.categoryId));
	}, [lesson, categories]);

	useEffect(() => {
		if (!lesson) return;
		const interval = setInterval(() => {
			updateSessionTime(lesson.tutorialId);
		}, 1000 * 60);
		return () => clearInterval(interval);
	}, [lesson]);

	return (
		<section>
			{lesson && category && (
				<div>
					<div className="flex justify-between">
						<div className="w-3/4">
							<h1>{category.name}</h1>
						</div>
						<SelectDevelopmentView
							value={view}
							setValue={(v) => setView(v as string)}
							simpleListBoxId="viewFilter"
							options={[
								{ value: "table", label: "Tables" },
								{ value: "raw", label: "Raw Data" },
							]}
						/>
					</div>
					<h2>{lesson.name}</h2>
					{view == "lesson" && (
						<div className="mt-8 relative">
							<iframe
								onLoad={handleFrameLoad}
								src={lessonUrl}
								id="frame"
								seamless
								scrolling="no"
								frameBorder="0"
								height="600"
								width="750"
								className="mx-auto"
							/>
							{!lessonFrameLoaded && (
								<div
									id="loader"
									className="absolute top-1/2 left-1/2 -ml-6 -mt-6 w-16 h-16 p-1 z-50">
									<div
										id="loader-overlay"
										className="absolute top-0 left-0 bg-black rounded-lg opacity-50 w-full h-full">
										<img
											src={`${aspUrl}/images/ajax-loader.gif`}
											alt="Busy"
											className="absolute w-12 h-12 top-[6px] left-[6px]"
										/>
									</div>
								</div>
							)}
						</div>
					)}

					{view == "raw" && (
						<RtkqResultViewer heading="Play Lesson View Model" result={result} />
					)}
				</div>
			)}
		</section>
	);
}
